import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { US_STATES } from "@/lib/listings";
import { toast } from "sonner";
import {
  Search, MapPin, Briefcase, DollarSign, Building2, Clock,
  ExternalLink, Bookmark, Share2, Sparkles, Loader2, X,
  ChevronLeft, ChevronRight, AlertCircle, Globe, Landmark, Building, Database
} from "lucide-react";

const JOB_TYPES = [
  { v: "", ar: "كل الأنواع", en: "All types" },
  { v: "full_time", ar: "دوام كامل", en: "Full-time" },
  { v: "part_time", ar: "دوام جزئي", en: "Part-time" },
  { v: "contract", ar: "عقد/مستقل", en: "Contract" },
  { v: "internship", ar: "تدريب", en: "Internship" },
  { v: "temporary", ar: "مؤقت", en: "Temporary" }
];

const SOURCES = [
  { v: "all",     ar: "كل المصادر", en: "All Jobs",   Icon: Globe },
  { v: "adzuna",  ar: "Adzuna",     en: "Adzuna",     Icon: Building },
  { v: "usajobs", ar: "USAJOBS",    en: "USAJOBS",    Icon: Landmark },
];

const SOURCE_BADGE = {
  adzuna:  { ar: "Adzuna",  en: "Adzuna",  cls: "bg-purple-500/15 text-purple-300" },
  usajobs: { ar: "USAJOBS", en: "USAJOBS", cls: "bg-blue-500/15 text-blue-300" },
  local:   { ar: "Arab Sanad", en: "Arab Sanad", cls: "bg-yellow-500/15 text-yellow-300" },
};

const PER_PAGE = 20;

const jobTypeLabel = (v, lang) => {
  const m = JOB_TYPES.find((x) => x.v === v);
  return m ? (lang === "ar" ? m.ar : m.en) : v;
};

const salaryDisplay = (j) => {
  if (j.salary_text) return j.salary_text;
  if (j.salary_min && j.salary_max) {
    if (j.salary_min >= 1000)
      return `$${(j.salary_min / 1000).toFixed(0)}k – $${(j.salary_max / 1000).toFixed(0)}k`;
    return `$${j.salary_min} – $${j.salary_max}/hr`;
  }
  if (j.salary_min) return `≥ $${Number(j.salary_min).toLocaleString()}`;
  return null;
};

const relativeDate = (iso, t) => {
  if (!iso) return null;
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  if (days === 0) return t("اليوم", "Today");
  if (days === 1) return t("منذ يوم", "1 day ago");
  if (days < 30) return t(`منذ ${days} يوم`, `${days} days ago`);
  const months = Math.floor(days / 30);
  return t(`منذ ${months} شهر`, `${months} mo ago`);
};

const shareJob = (j, t) => {
  const url = j.apply_url || window.location.href;
  if (navigator.share) {
    navigator.share({ title: `${j.title} – ${j.company}`, url }).catch(() => {});
  } else {
    navigator.clipboard?.writeText(url);
    toast.success(t("تم نسخ الرابط", "Link copied"));
  }
};

const JobCard = ({ j, t, lang, isSaved, onSaveToggle }) => {
  const salary = salaryDisplay(j);
  const srcBadge = SOURCE_BADGE[j.source] || SOURCE_BADGE.local;
  return (
    <div className="glass-card rounded-2xl p-5 hover:border-yellow-500/60 transition-all purple-gold-glow" data-testid={`job-card-${j.job_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-bold text-lg text-white truncate">{j.title}</h3>
            {j.featured && (
              <Badge className="bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0">
                <Sparkles size={10} className="ml-1" /> {t("مميز", "Hot")}
              </Badge>
            )}
            {j.remote && (
              <Badge className="bg-emerald-500/15 text-emerald-300">
                <Globe size={10} className="ml-1" /> {t("عن بُعد", "Remote")}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-300">
            <Building2 size={14} className="text-yellow-500" />
            <span className="font-medium">{j.company}</span>
          </div>
        </div>
        <button
          onClick={() => onSaveToggle(j)}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
            isSaved ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-yellow-400"
          }`}
          data-testid={`job-save-${j.job_id}`}
        >
          <Bookmark size={16} fill={isSaved ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
        {(j.city || j.state) && (
          <span className="flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{[j.city, j.state].filter(Boolean).join(", ")}</span>
        )}
        {j.job_type && (
          <span className="flex items-center gap-1"><Briefcase size={12} className="text-purple-400" />{jobTypeLabel(j.job_type, lang)}</span>
        )}
        {salary && (
          <span className="flex items-center gap-1 text-yellow-400 font-semibold">
            <DollarSign size={12} />{salary}
          </span>
        )}
        {j.posted_date && (
          <span className="flex items-center gap-1"><Clock size={12} />{relativeDate(j.posted_date, t)}</span>
        )}
        <span className={`ml-auto px-2 py-0.5 rounded-full capitalize text-[10px] ${srcBadge.cls}`}>
          {lang === "ar" ? srcBadge.ar : srcBadge.en}
        </span>
      </div>

      {j.description && <p className="text-sm text-slate-400 line-clamp-2 mb-4">{j.description}</p>}

      <div className="flex gap-2">
        {j.apply_url ? (
          <a href={j.apply_url} target="_blank" rel="noreferrer" className="flex-1">
            <Button className="w-full btn-primary" data-testid={`job-apply-${j.job_id}`}>
              {t("تقدّم الآن", "Apply Now")} <ExternalLink size={14} className="ml-2" />
            </Button>
          </a>
        ) : (
          <Button disabled className="flex-1 bg-slate-800 text-slate-500">{t("لا يوجد رابط تقديم", "No apply link")}</Button>
        )}
        <Button onClick={() => shareJob(j, t)} variant="outline"
          className="border-slate-700 text-white hover:text-purple-300 hover:border-purple-400/40"
          data-testid={`job-share-${j.job_id}`}>
          <Share2 size={14} />
        </Button>
      </div>
    </div>
  );
};

export default function JobsSearch() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();

  // Form-level (pending) filters; applied on Search submit.
  const [pending, setPending] = useState({
    query: "", state: "", city: "", job_type: "",
    salary_min: "", salary_max: "", category: "",
    remote: false, source: "all",
  });
  const [filters, setFilters] = useState(pending);
  const [page, setPage] = useState(0);

  const [data, setData] = useState(null);    // { total, page, per_page, sources, results }
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE, sources: filters.source };
      const map = {
        query: filters.query, state: filters.state, city: filters.city,
        job_type: filters.job_type, salary_min: filters.salary_min,
        salary_max: filters.salary_max, category: filters.category,
      };
      Object.entries(map).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      if (filters.remote) params.remote = true;
      const res = await axios.get(`${API}/jobs/search`, { params });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setData({ total: 0, page, per_page: PER_PAGE, results: [], errors: ["request_failed"], fallback_used: false, sources: {} });
    } finally { setLoading(false); }
  }, [filters, page]);

  const loadSaved = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/jobs/saved/ids`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedIds(new Set(res.data.ids));
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { search(); }, [search]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleSave = useCallback(async (j) => {
    if (!token) { navigate("/login"); return; }
    if (j.source && j.source !== "local") {
      toast.message(t("هذه الوظيفة من مصدر خارجي — افتحها على موقع المعلن", "Live external job — open it on the source site"));
      return;
    }
    const id = j.job_id;
    const isSaved = savedIds.has(id);
    try {
      if (!isSaved) {
        await axios.post(`${API}/jobs/saved/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setSavedIds((s) => new Set([...s, id]));
        toast.success(t("تم الحفظ", "Saved"));
      } else {
        await axios.delete(`${API}/jobs/saved/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSavedIds((s) => { const n = new Set(s); n.delete(id); return n; });
        toast.success(t("تم الحذف", "Removed"));
      }
    } catch { toast.error(t("فشلت العملية", "Action failed")); }
  }, [token, navigate, savedIds, t]);

  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none w-full";
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);
  const showingFrom = total === 0 ? 0 : page * PER_PAGE + 1;
  const showingTo   = Math.min((page + 1) * PER_PAGE, total);

  const adzunaInfo  = data?.sources?.adzuna;
  const usajobsInfo = data?.sources?.usajobs;

  return (
    <DashboardLayout title={t("البحث عن وظيفة", "Job Search")}>
      <div className="space-y-6" data-testid="jobs-search">
        {/* Hero — preserved styling */}
        <div className="relative glass-card rounded-3xl p-6 lg:p-10 overflow-hidden bg-luxury">
          <div className="absolute -top-40 -right-40 w-[460px] h-[460px] bg-yellow-500/20 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] bg-purple-500/20 blur-[140px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-3">
                <Briefcase size={12} className="ml-1" /> {t("سوق العمل", "Smart Jobs")}
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold mb-2 leading-tight text-luxury">
                {t("ابحث عن وظيفتك القادمة", "Find your next role")}
              </h2>
              <p className="text-slate-400 max-w-2xl">
                {t(
                  "وظائف حقيقية حيّة من Adzuna (القطاع الخاص) و USAJOBS (الحكومة الفيدرالية).",
                  "Live roles from Adzuna (private sector) and USAJOBS (federal government).",
                )}
              </p>
            </div>
            <Button onClick={() => navigate("/jobs/saved")} variant="outline"
              className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
              data-testid="goto-saved-jobs-btn">
              <Bookmark className="ml-2" size={18} /> {t("الوظائف المحفوظة", "Saved jobs")}
            </Button>
          </div>
        </div>

        {/* Source picker — pill row */}
        <div className="flex flex-wrap gap-2" data-testid="jobs-source-row">
          {SOURCES.map((s) => {
            const Icon = s.Icon;
            const active = pending.source === s.v;
            return (
              <button key={s.v}
                onClick={() => { const nx = { ...pending, source: s.v }; setPending(nx); setFilters(nx); setPage(0); }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition border ${
                  active
                    ? "bg-yellow-500/15 border-yellow-500/60 text-yellow-300"
                    : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500"
                }`}
                data-testid={`jobs-source-${s.v}`}
              >
                <Icon size={14} />{language === "ar" ? s.ar : s.en}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-slate-500 self-center">
            {adzunaInfo && (
              <span className="mr-2">
                {t("Adzuna:", "Adzuna:")} {adzunaInfo.enabled
                  ? `${(adzunaInfo.total || 0).toLocaleString()} ${t("وظيفة", "jobs")}`
                  : t("غير مفعّل", "not configured")}
              </span>
            )}
            {usajobsInfo && (
              <span>
                {t("USAJOBS:", "USAJOBS:")} {usajobsInfo.enabled
                  ? `${(usajobsInfo.total || 0).toLocaleString()} ${t("وظيفة", "jobs")}`
                  : t("غير مفعّل", "not configured")}
              </span>
            )}
          </span>
        </div>

        {/* Filters grid */}
        <div className="glass-card rounded-2xl p-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="jobs-filters">
          <input
            placeholder={t("المسمى الوظيفي أو الكلمة المفتاحية…", "Job title or keyword…")}
            value={pending.query}
            onChange={(e) => setPending({ ...pending, query: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { setFilters(pending); setPage(0); } }}
            className={`${inputCls} md:col-span-2`}
            data-testid="jobs-filter-query"
          />
          <select value={pending.state} onChange={(e) => setPending({ ...pending, state: e.target.value })}
            className={inputCls} data-testid="jobs-filter-state">
            <option value="">{t("كل الولايات", "All states")}</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
          </select>
          <input placeholder={t("المدينة", "City")} value={pending.city}
            onChange={(e) => setPending({ ...pending, city: e.target.value })}
            className={inputCls} data-testid="jobs-filter-city" />
          <select value={pending.job_type} onChange={(e) => setPending({ ...pending, job_type: e.target.value })}
            className={inputCls} data-testid="jobs-filter-type">
            {JOB_TYPES.map((j) => <option key={j.v} value={j.v}>{language === "ar" ? j.ar : j.en}</option>)}
          </select>
          <input placeholder={t("الفئة (مثل: nursing)", "Category (e.g. nursing)")}
            value={pending.category}
            onChange={(e) => setPending({ ...pending, category: e.target.value })}
            className={inputCls} data-testid="jobs-filter-category" />
          <input type="number" placeholder={t("راتب أدنى ($)", "Min salary ($)")}
            value={pending.salary_min}
            onChange={(e) => setPending({ ...pending, salary_min: e.target.value })}
            className={inputCls} data-testid="jobs-filter-salary-min" />
          <input type="number" placeholder={t("راتب أعلى ($)", "Max salary ($)")}
            value={pending.salary_max}
            onChange={(e) => setPending({ ...pending, salary_max: e.target.value })}
            className={inputCls} data-testid="jobs-filter-salary-max" />
          <label className="flex items-center gap-2 text-sm text-slate-300 px-3 col-span-2 md:col-span-1">
            <input type="checkbox" checked={pending.remote}
              onChange={(e) => setPending({ ...pending, remote: e.target.checked })}
              className="accent-yellow-500" data-testid="jobs-filter-remote" />
            <Globe size={14} className="text-emerald-400" /> {t("عن بُعد فقط", "Remote only")}
          </label>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex gap-2">
            <Button onClick={() => { setFilters(pending); setPage(0); }} className="btn-primary" data-testid="jobs-search-btn">
              <Search size={16} className="ml-2" /> {t("ابحث", "Search")}
            </Button>
            <Button
              onClick={() => {
                const empty = { query: "", state: "", city: "", job_type: "",
                  salary_min: "", salary_max: "", category: "", remote: false, source: "all" };
                setPending(empty); setFilters(empty); setPage(0);
              }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="jobs-reset-btn">
              <X size={14} className="ml-2" /> {t("مسح", "Reset")}
            </Button>
          </div>
          {total > 0 && (
            <span className="text-sm text-slate-400" data-testid="jobs-result-count">
              {t(
                `إجمالي ${total.toLocaleString("ar")} وظيفة — عرض ${showingFrom}–${showingTo}`,
                `${total.toLocaleString()} jobs — showing ${showingFrom}–${showingTo}`,
              )}
            </span>
          )}
        </div>

        {/* Fallback / errors notice */}
        {data?.fallback_used && (
          <div className="glass-card rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="jobs-fallback">
            <Database size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-100/90">
              {t(
                "نتائج المصدر الخارجي غير متاحة الآن — نعرض وظائف Arab Sanad المخزّنة كحل بديل.",
                "Live sources unavailable right now — showing the Arab Sanad seeded jobs as a fallback.",
              )}
            </p>
          </div>
        )}
        {data?.errors?.length > 0 && !data?.fallback_used && (
          <div className="glass-card rounded-2xl p-4 border border-red-500/30 bg-red-500/5 flex items-start gap-3" data-testid="jobs-errors">
            <AlertCircle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-100/90">
              {t("تعذّر الاستجابة من:", "Could not respond:")} {data.errors.join(", ")}
            </p>
          </div>
        )}

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Briefcase className="text-yellow-500" size={20} /> {t("النتائج", "Results")}
              <span className="text-sm text-slate-400 font-normal">({(data?.results?.length || 0)})</span>
            </h3>
            {loading && <Loader2 size={16} className="animate-spin text-yellow-400" />}
          </div>

          {loading && !data ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map((i) => <div key={i} className="glass-card rounded-2xl h-56 skeleton" />)}
            </div>
          ) : (data?.results?.length || 0) === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center text-slate-400" data-testid="jobs-empty">
              {t("لا توجد وظائف تطابق البحث — جرّب توسيع الفلاتر.", "No matching jobs — try widening your filters.")}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.results.map((j) => (
                <JobCard key={j.job_id} j={j} t={t} lang={language}
                  isSaved={savedIds.has(j.job_id)} onSaveToggle={toggleSave} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-5" data-testid="jobs-pagination">
              <Button variant="outline" size="sm" disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="jobs-page-prev">
                <ChevronRight size={16} className="ml-1" />{t("السابق", "Prev")}
              </Button>
              <span className="text-sm text-slate-300 px-3">
                {t(`صفحة ${page + 1} من ${totalPages.toLocaleString("ar")}`,
                   `Page ${page + 1} of ${totalPages.toLocaleString()}`)}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage((p) => p + 1)}
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="jobs-page-next">
                {t("التالي", "Next")}<ChevronLeft size={16} className="mr-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
